import Button from "atoms/buttons/button";
import ContactUsMessage from "organisms/resources/contact-us-message";
import DateUtils from "utilities/date-utils";
import GroupEligibleGroupService from "utilities/services/groups/group-eligible-groups-service";
import GroupWithExpirationRecord from "models/view-models/group-with-expiration-record";
import InputFormField from "molecules/form-fields/input-form-field";
import PreviewTeamMembers from "organisms/preview-team-members/preview-team-members";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import SelectFormField from "molecules/form-fields/select-form-field";
import moment from "moment";
import useFeatureFlags from "utilities/hooks/use-feature-flags";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { CollectionUtils } from "utilities/collection-utils";
import { DataConfiguration } from "constants/data-configuration";
import { SelectOption } from "atoms/forms/select";
import { StringUtils } from "@rsm-hcd/javascript-core";
import { ToastManager } from "utilities/toast/toast-manager";
import { siteMap } from "internal-sitemap";
import { t } from "utilities/localization-utils";
import { useGroupContext } from "utilities/contexts/group/group-context-provider";
import { useHistory } from "react-router-dom";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface VerifyTeamInfoProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const MaximumTeamNameLength = DataConfiguration.ExtraShortStringLength;
const BASE_CLASS = "c-verify-team-info";
const CREATE_NEW_TEAM = "CREATE_NEW_TEAM";

// #endregion Constants

// -----------------------------------------------------------------------------------------7
// #region Component
// -----------------------------------------------------------------------------------------

const VerifyTeamInfo: React.FC<VerifyTeamInfoProps> = () => {
    const history = useHistory();
    const { groupContext, setGroupContext } = useGroupContext();
    const [errorMessage, setErrorMessage] = useState<string | undefined>("");
    const isValidTeamName = StringUtils.isEmpty(errorMessage);
    const { useSelectPriorTeamDuringOnboarding } = useFeatureFlags();

    const { list: getEligibleGroupsApi } = GroupEligibleGroupService.useList();
    const [eligibleGroups, setEligibleGroups] = useState<
        GroupWithExpirationRecord[]
    >([]);
    const [eligibleGroupsOptions, setEligibleGroupsOptions] = useState<
        SelectOption[]
    >([]);

    const priorTeamsAvailable = useMemo(
        () =>
            CollectionUtils.isNotEmpty(eligibleGroupsOptions) &&
            useSelectPriorTeamDuringOnboarding,
        [eligibleGroupsOptions, useSelectPriorTeamDuringOnboarding]
    );

    const externalGroupIsSelected = useMemo(() => {
        return groupContext?.groupId !== null;
    }, [groupContext]);

    useEffect(() => {
        const listEligibleGroupsApi = async () => {
            try {
                const { resultObjects } = await getEligibleGroupsApi();
                if (CollectionUtils.isEmpty(resultObjects)) {
                    setEligibleGroups([]);
                    return;
                }
                setEligibleGroups(resultObjects);
            } catch (error) {
                setErrorMessage(t("getTeamsError"));
                ToastManager.error(t("getTeamsError"));
            }
        };

        if (useSelectPriorTeamDuringOnboarding) {
            listEligibleGroupsApi();
        }
    }, [getEligibleGroupsApi, useSelectPriorTeamDuringOnboarding]);

    useEffect(() => {
        if (
            CollectionUtils.isEmpty(eligibleGroups) ||
            !useSelectPriorTeamDuringOnboarding
        ) {
            setEligibleGroupsOptions([]);
            return; // Don't add a blank entry or "Create New Team" entry.
        }

        const options: SelectOption<any, any>[] = [
            {
                label: "",
                value: "",
                isPlaceholder: true,
            },

            ...eligibleGroups
                ?.sort((a, b) => {
                    return moment(b.subscriptionExpiresOn ?? moment.max()).diff(
                        moment(a.subscriptionExpiresOn ?? moment.max())
                    );
                })
                .map((group: GroupWithExpirationRecord) => {
                    return {
                        label: `${group.name} (Exp. ${DateUtils.formatIsoDate(
                            group?.subscriptionExpiresOn?.toString()
                        )})
                        `,
                        value: group.name,
                        data: group,
                    };
                }),
            {
                label: t("createNewTeam"),
                value: CREATE_NEW_TEAM,
                data: null,
            },
        ];
        setEligibleGroupsOptions(options);
    }, [eligibleGroups, useSelectPriorTeamDuringOnboarding]);

    const validateTeamName = (teamName?: string) => {
        if (StringUtils.isEmpty(teamName)) {
            return t("teamNameBlank");
        }

        if (teamName.length > MaximumTeamNameLength) {
            return t("teamNameMaxCharacters", {
                length: MaximumTeamNameLength,
            });
        }
    };

    const handleSelectChange = (e: any) => {
        if (e.value === CREATE_NEW_TEAM) {
            setGroupContext((prevState) => {
                return {
                    ...prevState,
                    teamName: "",
                    isNewTeam: true,
                    groupId: null,
                };
            });
        } else {
            setGroupContext((prevState) => {
                return {
                    ...prevState,
                    isNewTeam: false,
                    groupId: e.data.id,
                    teamName: e.data.name,
                };
            });
        }
    };

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        setErrorMessage("");
        const teamName = e.target.value;

        setGroupContext((prevState) => {
            return {
                ...prevState,
                isNewTeam: true,
                teamName,
            };
        });

        const validationError = validateTeamName(teamName);
        if (StringUtils.hasValue(validationError)) {
            setErrorMessage(validationError);
        }
    };

    const handleOnContinueClick = async () => {
        history.push(siteMap.teamOnboarding.manage);
    };

    const handleSelectExistingTeam = () => {
        setGroupContext({
            groupId: null,
            teamName: "",
            isNewTeam: false,
        });
    };
    return (
        <div className={BASE_CLASS}>
            <p className={`${BASE_CLASS}__title`}>
                {priorTeamsAvailable ? (
                    <SelectPriorTeamMessage />
                ) : (
                    <CreateNewTeamMessage />
                )}
            </p>
            <div className={`${BASE_CLASS}__input-container`}>
                {priorTeamsAvailable && !groupContext.isNewTeam ? (
                    <>
                        <SelectFormField
                            cssClassName={`${BASE_CLASS}__select`}
                            label={`${t("teamName").toUpperCase()}:`}
                            options={eligibleGroupsOptions}
                            onChange={handleSelectChange}
                            value={groupContext.teamName}
                        />
                        {externalGroupIsSelected && (
                            <PreviewTeamMembers
                                groupId={groupContext.groupId!}
                                groupName={groupContext.teamName}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <InputFormField
                            errorMessages={[errorMessage ?? ""]}
                            isValid={isValidTeamName}
                            label={`${t("teamName").toUpperCase()}:`}
                            maxLength={MaximumTeamNameLength}
                            onChange={handleInputChange}
                            placeholder={t("enterTeamName")}
                            value={groupContext.teamName}
                        />
                        {priorTeamsAvailable && (
                            <Button
                                cssClassName={`${BASE_CLASS}__input-container__button`}
                                style={ButtonStyles.None}
                                size={ButtonSizes.Small}
                                onClick={handleSelectExistingTeam}>
                                {t("selectExistingTeam")}
                            </Button>
                        )}
                    </>
                )}
            </div>
            <div>
                <Button
                    style={ButtonStyles.Primary}
                    size={ButtonSizes.Medium}
                    disabled={!StringUtils.hasValue(groupContext.teamName)}
                    onClick={handleOnContinueClick}>
                    {t("continue")}
                </Button>
            </div>
            <ContactUsMessage />
        </div>
    );
};

const SelectPriorTeamMessage: React.FC = (): JSX.Element => {
    return (
        <>
            {t("weHaveIdentifiedExistingTeamAccountAssociatedWithYourEmail")}
            <br />
            <b>{t("verifyTeamInformationOrCreateNewTeamAndClickContinue")}</b>
        </>
    );
};

const CreateNewTeamMessage: React.FC = (): JSX.Element => {
    return (
        <>
            {t("welcomeToLiNK")}
            <br />
            <b>{t("createANewTeamToStartYourSubscription")}</b>
        </>
    );
};

// #endregion Component

export default VerifyTeamInfo;
